import React, { useEffect, useRef, useState } from "react";
import { CameraFill, ChevronLeft, PlusLg, Trash } from "react-bootstrap-icons";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { useNavigate, useParams } from "react-router-dom";
import { decryptData, encryptData } from "../../utils/crypto";
import Utils from "../../utils";
import { useTranslation } from "react-i18next";
import {
  getStoreOwnerProfileDetails,
  storeOwnerUpdateProfile,
} from "../../services/authServices";
import CONFIG from "../../config";
import { notify } from "../../components/Common/Alerts";

const StoreOwnerProfile = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { t } = useTranslation();
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("theme") === "dark",
  );
  const [formData, setFormData] = useState({
    full_name: "",
    mobile: "",
    email: "",
    confirm_password: "",
    password: "",
  });
  const [errors, setErrors] = useState({
    mobile: "",
    email: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const [storeOwnerDetails, setStoreOwnerDetails] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const handleStorageChange = () => {
      setDarkMode(localStorage.getItem("theme") === "dark");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];

      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
    }
  };
  const handleDelete = (e) => {
    e.stopPropagation();
    setFile(null);
    setPreview(null);
  };

  const validateField = (name, value) => {
    switch (name) {
      case "mobile":
        const mobileRegex = /^\d{10}$/;
        if (!mobileRegex.test(value)) {
          return t("enterDigitMobileNumber");
        }
        return "";

      // case "password":
      //   if (value.length < 6)
      //     return t("passwordCharactersLong");
      //   if (!/[a-zA-Z]/.test(value))
      //     return t("passwordMustLetter");
      //   return "";

      // case "confirm_password":
      //   if (formData.password) {
      //     if (!value) return t("confirmPasswordIsRequired");
      //     if (value !== formData.password) return t("passwordsDoNotMatch");
      //   }
      //   return "";

      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value && !emailRegex.test(value)) {
          return t("pleaseEnterValidEmail");
        }
        return "";

      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value, { ...formData, [name]: value }),
    }));
  };

  useEffect(function () {
    getStoreOwnerProfileDetails()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        setStoreOwnerDetails(decrypted.data);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  useEffect(
    function () {
      if (storeOwnerDetails) {
        setFormData({
          full_name: storeOwnerDetails?.full_name,
          mobile: storeOwnerDetails?.mobile,
          email: storeOwnerDetails?.email,
        });
        setFile(storeOwnerDetails?.profile_image);
        setPreview(storeOwnerDetails?.profile_image);
      }
    },
    [storeOwnerDetails],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      mobile: validateField("mobile", formData.mobile),
      // confirm_password: formData.password
      //   ? validateField("confirm_password", formData.confirm_password)
      //   : "",
    };
    Utils.focusError();
    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some((err) => err);

    if (hasErrors) return;

    setBtnLoading(true);

    let data = {
      full_name: formData.full_name?.trim() || "",
      mobile: formData.mobile || "",
      email: formData.email?.trim() || "",
      password: formData.password || "",
      is_delete_icon: file ? 0 : 1,
    };
    let q = encryptData(data);

    const formPayload = new FormData();

    if (file) {
      formPayload.append("files", file);
    }
    formPayload.append("q", q);

    try {
      const response = await storeOwnerUpdateProfile(formPayload);
      const decrypted = decryptData(response.data.data);
      const storedData = JSON.parse(localStorage.getItem(CONFIG.login_data));

      const updatedData = {
        ...storedData,
        user: {
          ...storedData.user,
          full_name: decrypted?.data?.full_name,
          mobile: decrypted?.data?.mobile,
          email: decrypted?.data?.email,
        },
      };
      localStorage.setItem(CONFIG.login_data, JSON.stringify(updatedData));
      notify.success(decrypted.message);
      setErrors("");
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <div>
      <div className="mx-auto space-y-8 animate-fade-up pb-12">
        <div className="flex items-center gap-4 md:gap-6 flex-shrink-0 px-2 mb-2 hidden md:flex">
          <button
            onClick={() => navigate("/dashboard")}
            className="group flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:px-4 md:py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all active:scale-95 shadow-sm"
          >
            <ChevronLeft
              size={18}
              className="group-hover:-translate-x-1 transition-transform"
            />
            <span className="hidden ml-2 font-bold text-sm">{t("back")}</span>
          </button>

          <div className="flex-1">
            <h2 className="text-xl md:text-3xl font-black text-slate-800 dark:text-white tracking-tight leading-tight">
              {t("updateProfile")}{" "}
            </h2>
          </div>
        </div>

        <section className="bg-white dark:bg-slate-900 rounded-[32px] border border-slate-100 dark:border-white/5 p-6 lg:p-10 shadow-sm transition-all">
          {/* Header Section */}
          <div className="flex items-center justify-between mb-10 pb-6 border-b border-slate-50 dark:border-white/5">
            <div className="flex items-center gap-4">
              <div></div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-3 flex flex-col items-center justify-start pt-2">
              <div
                className="relative group cursor-pointer"
                onClick={() => inputRef.current.click()}
              >
                <input
                  type="file"
                  ref={inputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />

                <div className="w-36 h-36 bg-gray-50 rounded-full border-4 border-white shadow-xl flex items-center justify-center overflow-hidden transition-transform group-hover:scale-105">
                  {preview ? (
                    <img
                      src={preview}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <CameraFill size={32} className="text-gray-200" />
                  )}
                </div>

                {!file && (
                  <div className="absolute bottom-2 right-2 bg-brand-orange text-white p-2.5 rounded-full border-4 border-white shadow-lg">
                    <PlusLg size={16} />
                  </div>
                )}

                {file && (
                  <div
                    onClick={handleDelete}
                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full border-4 border-white shadow-lg"
                  >
                    <Trash size={14} />
                  </div>
                )}
              </div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-6">
                {t("profilePicture")}
              </p>
            </div>

            <div className="lg:col-span-9">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <div className="flex flex-col gap-2">
                  <label htmlFor="full_name" className="input-label">
                    {t("fullName")}{" "}
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    placeholder={t("enterFullName")}
                    className={`input-field ${errors.full_name ? "input-error" : ""}`}
                    value={formData?.full_name || ""}
                    onChange={handleChange}
                    maxLength={30}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="mobile" className="input-label">
                    {t("mobileNumber")}{" "}
                  </label>
                  <input
                    type="number"
                    name="mobile"
                    placeholder={t("enterMobileNumber")}
                    className={`input-field 
                    ${errors.mobile ? "input-error border-red-500" : ""}
                    disabled:bg-white disabled:text-black disabled:opacity-100 disabled:cursor-not-allowed dark:disabled:bg-black dark:disabled:text-white`}
                    value={formData?.mobile || ""}
                    onChange={handleChange}
                    min={0}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    disabled
                  />
                  {errors.mobile && (
                    <span className="text-xs text-red-500 mt-1">
                      {errors.mobile}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2 md:col-span-2">
                  <label htmlFor="email" className="input-label">
                    {t("email")}
                  </label>
                  <input
                    type="text"
                    name="email"
                    placeholder={t("enterYourEmail")}
                    className={`input-field ${errors.email ? "input-error" : ""}`}
                    value={formData?.email || ""}
                    onChange={handleChange}
                  />
                  {errors.email && (
                    <span className="text-danger">{errors.email}</span>
                  )}
                </div>

                {/* <div className="flex flex-col gap-2">
                  <label
                    htmlFor="password"
                    name="password"
                    className="input-label"
                  >
                    {t("password")}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="••••••••"
                      className={`input-field w-full pr-10 ${errors.password ? "input-error" : ""}`}
                      value={formData?.password || ""}
                      onChange={handleChange}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? (
                        <Eye size={18} />
                      ) : (
                        <EyeSlash size={18} />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <span className="text-danger">{errors.password}</span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="confirmPassword"
                    name="confirmPassword"
                    className="input-label"
                  >
                    {t("confirmPassword")}{" "}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className={`input-field w-full pr-10 ${errors.confirm_password ? "input-error" : ""}`}
                      name="confirm_password"
                      value={formData?.confirm_password || ""}
                      onChange={handleChange}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? (
                        <Eye size={18} />
                      ) : (
                        <EyeSlash size={18} />
                      )}
                    </button>
                  </div>
                  {errors.confirm_password && (
                    <span className="text-danger">
                      {errors.confirm_password}
                    </span>
                  )}
                </div> */}

                <div className="md:col-span-2 flex items-center justify-end gap-3 pt-6 border-t border-slate-50 dark:border-white/5 mt-4">
                  <button
                    onClick={handleSubmit}
                    disabled={btnLoading}
                    className="px-8 py-2.5 text-sm font-bold text-white bg-orange-500 hover:bg-orange-600 rounded-xl shadow-lg shadow-orange-500/30 transition-all flex items-center gap-2"
                  >
                    {btnLoading && (
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    )}
                    {btnLoading ? t("submitting") : t("update")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default StoreOwnerProfile;
